import { z } from "zod";

/**
 * Sprint 5 — Klyra-side LIS receiving infrastructure.
 *
 * The on-prem Tauri bridge agent (not in this monorepo) authenticates
 * with `bridge_token` and POSTs ingestion payloads to /bridge/results.
 * That endpoint resolves the sample_id against `lab_orders.accession_number`
 * (Sprint 3) and delegates to createLabResultValues which runs the
 * full Sprint 1-4 pipeline (range resolution, critical alerts, delta
 * checks, reflex triggers).
 */

// ===== Instrument resource =====

/**
 * What the registered device is (0154). 'lab_analyzer' speaks ASTM through
 * the lab bridge agent; 'imaging_modality' speaks DICOM (C-STORE + MWL)
 * through the radiology bridge agent. Token handling is identical.
 */
export const instrumentKindSchema = z.enum([
	"lab_analyzer",
	"imaging_modality",
]);
export type InstrumentKind = z.infer<typeof instrumentKindSchema>;

export const instrumentSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	model: z.string(),
	manufacturer: z.string(),
	serial_number: z.string().nullable(),
	kind: instrumentKindSchema,
	/**
	 * NOT returned on list / get — only on create + rotate so the owner
	 * can copy it during onboarding. Reads after creation get this field
	 * as null so a screen leak never exposes the credential.
	 */
	bridge_token: z.string().nullable(),
	last_seen_at: z.string().datetime().nullable(),
	is_active: z.boolean(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
	/** Optional embedded mappings — populated by GET /instruments?withMappings. */
	mappings: z.array(z.unknown()).optional(),
});
export type InstrumentApi = z.infer<typeof instrumentSchema>;

export const createInstrumentInputSchema = z.object({
	name: z.string().min(1).max(200),
	model: z.string().min(1).max(120),
	manufacturer: z.string().min(1).max(120),
	serial_number: z.string().max(120).nullable().optional(),
	kind: instrumentKindSchema.optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreateInstrumentInput = z.infer<typeof createInstrumentInputSchema>;

export const updateInstrumentInputSchema = createInstrumentInputSchema
	.partial()
	.extend({ is_active: z.boolean().optional() });
export type UpdateInstrumentInput = z.infer<typeof updateInstrumentInputSchema>;

// ===== Test-code mappings =====

export const instrumentTestMappingSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	instrument_id: z.string().uuid(),
	instrument_test_code: z.string(),
	klyra_test_name: z.string(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type InstrumentTestMappingApi = z.infer<
	typeof instrumentTestMappingSchema
>;

export const upsertInstrumentMappingInputSchema = z.object({
	instrument_id: z.string().uuid(),
	instrument_test_code: z.string().min(1).max(80),
	klyra_test_name: z.string().min(1).max(200),
	notes: z.string().max(2000).nullable().optional(),
});
export type UpsertInstrumentMappingInput = z.infer<
	typeof upsertInstrumentMappingInputSchema
>;

// ===== Bridge ingestion payload =====

/**
 * One numeric measurement parsed from an ASTM result frame. Field names
 * mirror the ASTM E1394 R-record vocabulary so the bridge agent can map
 * raw frame text to JSON 1:1.
 *
 * `flag` is the analyzer's internal flag string ("H", "L", "HH", "LL",
 * "N"). Klyra ignores it on insert — our server recomputes the
 * abnormal_flag from the configured reference range — but we accept it
 * for audit logging.
 */
export const ingestionValueSchema = z.object({
	test_code: z.string().min(1).max(80),
	value: z.number(),
	unit: z.string().max(60).nullable().optional(),
	ref_low: z.number().nullable().optional(),
	ref_high: z.number().nullable().optional(),
	flag: z.string().max(8).nullable().optional(),
	measured_at: z.string().datetime().optional(),
});
export type IngestionValue = z.infer<typeof ingestionValueSchema>;

/**
 * Bridge → Klyra ingestion payload. One call per sample. The bridge
 * groups raw ASTM R-records by sample_id from the parent P/O records and
 * batches them so Klyra's range resolution + reflex firing all happen
 * in one transaction per sample.
 */
export const ingestionPayloadSchema = z.object({
	sample_id: z.string().min(1).max(80),
	measured_at: z.string().datetime().optional(),
	values: z.array(ingestionValueSchema).min(1).max(60),
});
export type IngestionPayload = z.infer<typeof ingestionPayloadSchema>;

/** Response from /bridge/results — light, surfaces matches + warnings. */
export const ingestionResponseSchema = z.object({
	matched_order_id: z.string().uuid().nullable(),
	accession_number: z.string().nullable(),
	values_inserted: z.number().int(),
	/**
	 * Warnings the bridge should log for review:
	 *   - 'no_match'      — sample_id not found in any pending order
	 *   - 'no_mapping'    — instrument_test_code missing a Klyra mapping
	 *   - 'order_complete' — sample_id matches a completed order (results late)
	 */
	warnings: z.array(z.string()),
});
export type IngestionResponse = z.infer<typeof ingestionResponseSchema>;

// ===== Device profiles (one-click default test-code mappings per analyzer) =====
//
// Pre-built ASTM test-code → Klyra test-name maps for common analyzers, so an
// instrument can be wired up at the lab in one click instead of typing dozens
// of mappings. Codes are matched case-insensitively at ingestion; the lab can
// edit/add rows afterwards. Klyra test names are sensible canonical defaults.

export const deviceProfileSchema = z.enum([
	"snibe_maglumi",
	"spinreact_chem",
	"hema_cbc",
]);
export type DeviceProfileKey = z.infer<typeof deviceProfileSchema>;

export interface DeviceProfile {
	key: DeviceProfileKey;
	label: string;
	/** [instrument_test_code, klyra_test_name] */
	mappings: Array<[string, string]>;
}

export const DEVICE_PROFILES: Record<DeviceProfileKey, DeviceProfile> = {
	snibe_maglumi: {
		key: "snibe_maglumi",
		label: "Snibe MAGLUMI (immunoassay)",
		mappings: [
			["TSH", "TSH"],
			["FT3", "Free T3"],
			["FT4", "Free T4"],
			["T3", "T3"],
			["T4", "T4"],
			["FSH", "FSH"],
			["LH", "LH"],
			["PRL", "Prolactin"],
			["E2", "Estradiol (E2)"],
			["PROG", "Progesterone"],
			["TESTO", "Testosterone"],
			["Ferritin", "Ferritin"],
			["VitD", "Vitamin D (25-OH)"],
			["VB12", "Vitamin B12"],
			["FOL", "Folate"],
			["INS", "Insulin"],
			["Cortisol", "Cortisol"],
			["PSA", "PSA"],
			["fPSA", "Free PSA"],
			["AFP", "AFP"],
			["CEA", "CEA"],
			["CA125", "CA 125"],
			["CA153", "CA 15-3"],
			["CA199", "CA 19-9"],
			["HCG", "Beta-hCG"],
			["Anti-TPO", "Anti-TPO"],
			["Anti-TG", "Anti-Tg"],
			["HBsAg", "HBsAg"],
			["Anti-HCV", "Anti-HCV"],
			["HIV", "HIV Ag/Ab"],
		],
	},
	spinreact_chem: {
		key: "spinreact_chem",
		label: "Spinreact / chemistry analyzer",
		mappings: [
			["GLU", "Glucose (FBS)"],
			["CHOL", "Cholesterol"],
			["TG", "Triglycerides"],
			["HDL", "HDL"],
			["LDL", "LDL"],
			["TSB", "Total Bilirubin"],
			["BILD", "Direct Bilirubin"],
			["BiliD", "Direct Bilirubin"],
			["GPT", "ALT (GPT)"],
			["ALT", "ALT (GPT)"],
			["GOT", "AST (GOT)"],
			["AST", "AST (GOT)"],
			["ALP", "Alkaline Phosphatase"],
			["Urea", "Urea"],
			["BUN", "BUN"],
			["Crea", "Creatinine"],
			["UA", "Uric Acid"],
			["Ca", "Calcium"],
			["PO4", "Phosphorus"],
			["Na", "Sodium"],
			["K", "Potassium"],
			["CL", "Chloride"],
			["TP", "Total Protein"],
			["ALB", "Albumin"],
			["AMYL", "Amylase"],
			["LIPA", "Lipase"],
			["LDH", "LDH"],
			["Fe", "Iron"],
			["TIBC", "TIBC"],
			["HbA1c", "HbA1c"],
			["CRP", "CRP"],
			["RF", "RF"],
			["ASOT", "ASO"],
			["GGT", "GGT"],
			["CK", "CK"],
			["Mg", "Magnesium"],
			["Zinc", "Zinc"],
			["Copper", "Copper"],
		],
	},
	hema_cbc: {
		key: "hema_cbc",
		label: "Hematology / CBC analyzer",
		mappings: [
			["WBC", "WBC"],
			["RBC", "RBC"],
			["HGB", "Hemoglobin"],
			["HCT", "Hematocrit"],
			["MCV", "MCV"],
			["MCH", "MCH"],
			["MCHC", "MCHC"],
			["RDW-CV", "RDW-CV"],
			["RDW-SD", "RDW-SD"],
			["PLT", "Platelets"],
			["MPV", "MPV"],
			["PDW", "PDW"],
			["PCT", "PCT"],
			["Lym#", "Lymphocytes #"],
			["Lym%", "Lymphocytes %"],
			["Mid#", "Mid #"],
			["Mid%", "Mid %"],
			["Gran#", "Granulocytes #"],
			["Gran%", "Granulocytes %"],
			["NEU%", "Neutrophils %"],
			["NEU#", "Neutrophils #"],
			["EOS%", "Eosinophils %"],
			["BAS%", "Basophils %"],
		],
	},
};

export const applyDeviceProfileInputSchema = z.object({
	profile: deviceProfileSchema,
});
export type ApplyDeviceProfileInput = z.infer<
	typeof applyDeviceProfileInputSchema
>;

// ===== Bidirectional LIS: host-query worklist (analyzer asks "what tests?") =====

export const bridgeWorklistTestSchema = z.object({
	/** The code to send back to the analyzer (its own code when mapped). */
	code: z.string(),
	/** Canonical Klyra test name (for logging/debug). */
	name: z.string(),
});
export type BridgeWorklistTest = z.infer<typeof bridgeWorklistTestSchema>;

export const bridgeWorklistResponseSchema = z.object({
	sample_id: z.string(),
	found: z.boolean(),
	patient_name: z.string().nullable(),
	tests: z.array(bridgeWorklistTestSchema),
});
export type BridgeWorklistResponse = z.infer<
	typeof bridgeWorklistResponseSchema
>;
